import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, ArrowRight, X, Lightning, Bell, CalendarCheck, TrendUp } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ── Milestone Celebration ── */
export const MilestoneBanner = ({ milestone, onDismiss }) => {
  if (!milestone) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="bg-gradient-to-r from-[#235347] to-[#2D6A5A] rounded-xl p-4 text-white mb-4"
        data-testid="milestone-banner"
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
            <Trophy size={16} weight="fill" className="text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-0.5">Milestone</p>
            <p className="text-sm font-semibold">{milestone.label}</p>
            <p className="text-xs text-white/70 mt-0.5">{milestone.description}</p>
          </div>
          <button onClick={onDismiss} className="p-1 text-white/40 hover:text-white/80 transition-colors flex-shrink-0">
            <X size={14} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ── Pending Nudges ── */
const NUDGE_ICONS = {
  pending_confirmation: Lightning,
  checkin_overdue: CalendarCheck,
  insight_available: TrendUp,
};

export const NudgeCards = ({ nudges }) => {
  const navigate = useNavigate();
  if (!nudges || nudges.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="nudge-cards">
      {nudges.slice(0, 3).map((nudge, i) => {
        const Icon = NUDGE_ICONS[nudge.type] || Bell;
        return (
          <button
            key={i}
            onClick={() => navigate(nudge.action)}
            className="w-full bg-white border border-slate-150 rounded-xl p-3 flex items-center gap-3 hover:border-slate-300 transition-colors text-left group"
            data-testid={`nudge-${nudge.type}`}
          >
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
              nudge.priority === 'high' ? 'bg-amber-50' : 'bg-slate-50'
            }`}>
              <Icon size={14} weight="duotone" className={nudge.priority === 'high' ? 'text-amber-600' : 'text-slate-500'} />
            </div>
            <p className="text-sm text-slate-600 flex-1">{nudge.message}</p>
            <ArrowRight size={14} className="text-slate-300 group-hover:text-slate-500 transition-colors flex-shrink-0" />
          </button>
        );
      })}
    </div>
  );
};

/* ── Weekly Briefing Card ── */
export const WeeklyBriefing = ({ briefing }) => {
  const [expanded, setExpanded] = useState(false);
  if (!briefing || briefing.conversations === 0) return null;
  return (
    <div className="bg-white border border-slate-150 rounded-xl overflow-hidden" data-testid="weekly-briefing">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center gap-3 text-left hover:bg-slate-50/50 transition-colors"
      >
        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
          <CalendarCheck size={16} className="text-blue-600" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-0.5">Weekly Briefing</p>
          <p className="text-sm text-slate-600">{briefing.summary}</p>
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
              <div className="flex gap-4 text-center">
                <div className="flex-1">
                  <p className="text-lg font-semibold text-slate-800">{briefing.conversations}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Chats</p>
                </div>
                <div className="flex-1">
                  <p className="text-lg font-semibold text-slate-800">{briefing.check_ins}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Check-ins</p>
                </div>
                <div className="flex-1">
                  <p className="text-lg font-semibold text-slate-800">{briefing.patterns_detected}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Patterns</p>
                </div>
              </div>
              {briefing.trending?.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Active Topics</p>
                  <div className="flex flex-wrap gap-1.5">
                    {briefing.trending.map((t, i) => (
                      <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {briefing.suggestions?.length > 0 && (
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Suggested Next</p>
                  {briefing.suggestions.map((s, i) => (
                    <p key={i} className="text-xs text-slate-500 leading-relaxed">{s}</p>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-slate-300 text-center">{briefing.period}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Smart Upgrade Prompt ── */
export const UpgradePrompt = ({ upgrade, plan }) => {
  const navigate = useNavigate();
  // Don't show if already paid/foundational, or not enough value demonstrated
  if (!upgrade?.eligible) return null;
  if (plan === 'active_paid' || plan === 'foundational') return null;

  const valueMessages = {
    second_session: 'Multiple conversations',
    first_upload: 'Report analysis',
    first_pattern: 'Pattern detection',
    third_visit: 'Regular usage',
  };

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white" data-testid="upgrade-prompt">
      <p className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-2">Continue your progress</p>
      <p className="text-sm font-medium mb-1">Kindra is learning your health context</p>
      <p className="text-xs text-white/60 leading-relaxed mb-4">
        Your health memory, insights, and patterns keep improving with continued use.
      </p>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {upgrade.events?.map((e, i) => (
          <span key={i} className="px-2 py-0.5 bg-white/10 rounded text-[10px] text-white/70">
            {valueMessages[e] || e}
          </span>
        ))}
      </div>
      <div className="space-y-2">
        <p className="text-[10px] text-white/40 uppercase tracking-wider">What you keep with a subscription</p>
        <div className="grid grid-cols-2 gap-1.5 text-xs text-white/70">
          <span>Ongoing health memory</span>
          <span>Deeper insights over time</span>
          <span>Weekly health briefings</span>
          <span>Pattern detection</span>
        </div>
      </div>
      <button
        onClick={() => navigate('/billing')}
        className="mt-4 w-full bg-white text-slate-900 rounded-lg py-2.5 text-sm font-medium hover:bg-white/90 transition-colors"
        data-testid="upgrade-cta"
      >
        Continue with Kindra — $14.99/mo
      </button>
    </div>
  );
};

/* ── Hook to load engagement data ── */
export function useEngagement() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/engagement/status`);
      setData(d);
    } catch (err) {
      // Fail silently — engagement is non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  const ackMilestone = useCallback(async (id) => {
    try {
      await axios.post(`${API}/engagement/ack-milestone`, { milestone_id: id });
      setData(prev => prev ? {
        ...prev,
        milestones: { ...prev.milestones, new: null }
      } : prev);
    } catch {}
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, ackMilestone, refetch: fetch };
}
